import React from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface Permission {
  code: string;
  name: string;
  description: string;
  module: string;
  actions: string[];
}

/**
 * Hook to check user permissions
 */
export const usePermissions = () => {
  const { user } = useAuth();

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permissionCode: string): boolean => {
    if (!user) return false;
    
    // Admin role has all permissions
    if (user.role === 'Admin') return true;
    
    // Check if user has the specific permission
    return user.permissions?.includes(permissionCode) || false;
  };

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = (permissionCodes: string[]): boolean => {
    if (!user) return false;
    
    // Admin role has all permissions
    if (user.role === 'Admin') return true;
    
    // Check if user has any of the permissions
    return permissionCodes.some(code => user.permissions?.includes(code)) || false;
  };

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (permissionCodes: string[]): boolean => {
    if (!user) return false;
    
    // Admin role has all permissions
    if (user.role === 'Admin') return true;
    
    // Check if user has all the permissions
    return permissionCodes.every(code => user.permissions?.includes(code)) || false;
  };

  /**
   * Check if user can perform a specific action on a module
   */
  const canPerformAction = (module: string, action: string): boolean => {
    const permissionCode = `${module.toLowerCase()}.${action.toLowerCase()}`;
    return hasPermission(permissionCode);
  };

  /**
   * Get all user permissions
   */
  const getUserPermissions = (): string[] => {
    if (!user) return [];
    
    // Admin role has all permissions - we'll return a comprehensive list
    if (user.role === 'Admin') {
      return [
        // Contracts
        'contracts.view', 'contracts.create', 'contracts.edit', 'contracts.delete', 'contracts.approve',
        'contracts.files.view', 'contracts.files.upload', 'contracts.files.delete',
        'contracts.notes.view', 'contracts.notes.create', 'contracts.notes.edit', 'contracts.notes.delete',
        // Suppliers
        'suppliers.view', 'suppliers.create', 'suppliers.edit', 'suppliers.delete',
        // Reports
        'reports.view', 'reports.export',
        // Master Data
        'masterdata.view', 'masterdata.edit',
        // User Management
        'users.view', 'users.create', 'users.edit', 'users.delete',
        // Role Management
        'roles.view', 'roles.create', 'roles.edit', 'roles.delete', 'roles.matrix.edit',
        // System
        'system.settings', 'system.audit',
        // Profile
        'profile.view', 'profile.edit'
      ];
    }
    
    return user.permissions || [];
  };

  /**
   * Check if user is admin
   */
  const isAdmin = (): boolean => {
    return user?.role === 'Admin';
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canPerformAction,
    getUserPermissions,
    isAdmin,
    user
  };
};

/**
 * Permission-based conditional rendering component
 */
export interface PermissionGuardProps {
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  permissions = [],
  requireAll = false,
  fallback = null,
  children
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions.length > 0) {
    hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};